import React from 'react';
import { Link } from 'gatsby';
import { SignupForm } from '../signup';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import Facebook from '../../images/icons/icon-facebook.svg';
import Twitter from '../../images/icons/icon-twitter.svg';
import Instagram from '../../images/icons/icon-instagram.svg';
import { SmallGreenArrow } from '../../components/layout/arrows';
import { StaticImage } from 'gatsby-plugin-image';

const Year = new Date().getFullYear();

const footerLink = 'text-sm text-brand-blue-1 hover:text-white sm:w-40 block';

const Footer = ({ footerOptions }) => {
  return (
    <>
      <footer
        className="bg-brand-blue-4 relative overflow-clip px-2.5 sm:px-0"
        aria-labelledby="footerHeading"
      >
        <h2 id="footerHeading" className="sr-only">
          Footer
        </h2>
        {!footerOptions?.hideSignup && (
          <div className="mx-auto relative pt-2.5 sm:pt-20 sm:max-w-3xl lg:max-w-7xl xl:mx-auto sm:px-10">
            <SmallGreenArrow>
              <div className="z-10 bg-white pb-4 items-center px-6 sm:py-10 md:grid md:grid-cols-12 xl:px-14 xl:gap-10">
                <div className="col-span-5">
                  <p className="text-2xl font-bold text-brand-blue-4 leading-relaxed py-6">
                    Sign up for GrowSF's weekly roundup of important SF news
                  </p>
                </div>
                <div className="col-span-7">
                  <div className="lg:grid xl:grid-cols-2 xl:gap-10">
                    <div>
                      <p className="mt-3 text-sm font-bold text-gray-700 uppercase">
                        Sign up for our weekly newsletter
                      </p>
                      <SignupForm formStyle="home" formId="_signup_weekly" />
                    </div>
                    <div>
                      <p className="mt-3 text-sm font-bold text-gray-700 uppercase">
                        Follow us on social media
                      </p>
                      <div className="flex">
                        <OutboundLink
                          href="https://www.twitter.com/growsf"
                          className="mr-2 hover:opacity-60 p-5 inline-block"
                        >
                          <img src={Twitter} alt="Twitter" />
                        </OutboundLink>
                        <OutboundLink
                          href="https://www.facebook.com/growsforg"
                          className="mr-2 hover:opacity-60 p-5 inline-block"
                        >
                          <img src={Facebook} alt="Facebook" />
                        </OutboundLink>
                        <OutboundLink
                          href="https://www.instagram.com/grow_sf"
                          className="mr-2 hover:opacity-60 p-5 inline-block"
                        >
                          <img src={Instagram} alt="Instagram" />
                        </OutboundLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SmallGreenArrow>
          </div>
        )}

        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 z-10 relative">
          <div className="grid grid-cols-2 gap-4 gap-y-8 sm:grid-cols-3 md:grid-cols-5">
            <div>
              <h3 className="text-sm font-semibold text-white tracking-wider uppercase">
                What We're About
              </h3>
              <ul className="mt-4 space-y-4">
                <li>
                  <Link to="/mission/" className={footerLink}>
                    Mission
                  </Link>
                </li>
                <li>
                  <Link to="/issues/" className={footerLink}>
                    Issues
                  </Link>
                </li>
                <li>
                  <Link to="/campaigns/" className={footerLink}>
                    Campaigns
                  </Link>
                </li>
                <li>
                  <Link to="/pulse/" className={footerLink}>
                    Polls
                  </Link>
                </li>
                <li>
                  <Link to="/voter-guide/" className={footerLink}>
                    Voter Guide
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white tracking-wider uppercase">
                Follow Us
              </h3>
              <ul className="mt-4 space-y-4">
                <li>
                  <OutboundLink
                    href="https://twitter.com/GrowSF"
                    className={footerLink}
                    target="_blank"
                  >
                    Twitter
                  </OutboundLink>
                </li>
                <li>
                  <OutboundLink
                    href="https://report.growsf.org/"
                    className={footerLink}
                    target="_blank"
                  >
                    Newsletter
                  </OutboundLink>
                </li>
                <li>
                  <Link to="/news/" className={footerLink}>
                    News
                  </Link>
                </li>
                <li>
                  <Link to="/research/" className={footerLink}>
                    Research
                  </Link>
                </li>
                <li>
                  <OutboundLink
                    href="https://thebolditalic.com/"
                    className={footerLink}
                    target="_blank"
                  >
                    The Bold Italic
                  </OutboundLink>
                </li>
                <li>
                  <Link to="/team/" className={footerLink}>
                    Team
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white tracking-wider uppercase">
                Get Involved
              </h3>
              <ul className="mt-4 space-y-4">
                <li>
                  <OutboundLink
                    href="https://forms.gle/xYvN55oz8Qo5ESCk9"
                    className={footerLink}
                    target="_blank"
                  >
                    Volunteer
                  </OutboundLink>
                </li>
                <li>
                  <Link to="/donate/" className={footerLink}>
                    Donate
                  </Link>
                </li>
                <li>
                  <Link to="/careers/" className={footerLink}>
                    Careers
                  </Link>
                </li>
                <li>
                  <OutboundLink href="mailto:contact@growsf.org" className={footerLink}>
                    Email Us
                  </OutboundLink>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white tracking-wider uppercase">
                Helpful Links
              </h3>
              <ul className="mt-4 space-y-4">
                <li>
                  <Link to="/howto/file-a-police-report/" className={footerLink}>
                    How To File a Police Report
                  </Link>
                </li>
                <li>
                  <Link to="/howto/vote/" className={footerLink}>
                    Register to Vote
                  </Link>
                </li>
                <li>
                  <Link to="/welcome/" className={footerLink}>
                    Welcome to San Francisco
                  </Link>
                </li>
                <li>
                  <Link
                    to="/sf-district-supervisor-map/"
                    alt="Find my Supervisor"
                    className={footerLink}
                  >
                    Supervisor District Map
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white tracking-wider uppercase">Misc</h3>
              <ul className="mt-4 space-y-4">
                <li>
                  <Link to="/privacy-policy/" className={footerLink}>
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/sitemap/sitemap-index.xml" className={footerLink}>
                    Sitemap
                  </Link>
                </li>
                <li>
                  <Link to="/media-kit/" className={footerLink}>
                    Media Kit
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 md:flex md:items-center md:justify-between">
            <p className="mt-8 text-base text-white md:mt-0 md:order-1">
              &copy; {Year} GrowSF. All rights reserved.
            </p>
          </div>
        </div>
        <StaticImage
          className="w-full object-contain z-0 !absolute -bottom-2 -right-24 sm:w-auto sm:scale-75 sm:-bottom-16 sm:-right-32"
          src="../../images/core/sf-illustration-green.png"
          alt="Line art illustration of San Francisco"
        />
      </footer>
    </>
  );
};

export default Footer;
